import React, { memo, useMemo } from 'react'
import { Card, Button, Input, Table, Avatar, Spin, Select, Tag, Tooltip } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import './TopCostUsers.scss'

const { Search } = Input
const { Option } = Select

const formatModelName = (modelString) => {
  if (!modelString) return ''
  return modelString
    .replace(/-/g, ' ')
    .replace(/\bpreview\b/gi, '')
    .replace(/\blatest\b/gi, '')
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const TopCostUsers = memo(({
  title = 'Top AI Users (Cost)',
  data,
  loading,
  onExportClick,
  onSearch,
  searchTerm,
  pagination,
  onPageChange,
  tableFilter,
  onTableFilterChange,
  activeTypes = [],
  selectedType,
  onTypeChange,
  orgList,
  selectedOrg,
  onOrgChange
}) => {
  const timeFilters = ['All Time', 'Today', 'This Week', 'This Month', 'This Year']

  const tableData = useMemo(() => {
    if (!data?.topUsers) return []

    return data.topUsers.map((user, index) => ({
      key: user.id || index,
      rank: user.rank || (pagination?.current - 1) * pagination?.pageSize + index + 1,
      user: user.name || 'Unknown User',
      email: user.email || '',
      avatar: user.avatar || '',
      avgCost: user.totalCreditsUsed || 0,
      totalCost: user.totalCost || 0,
      level: user.level || 'N/A',
      tokens: user.tokens || 0,
      model: user.model || '',
      title: user.title || '',
      prompt: user.prompt || '',
      depth: user.depth,
      organization: user.organization,
      duration: user.duration,
      totalPages: user.totalPages,
      elearningCourse: user.elearningCourse
    }))
  }, [data, pagination])

  const columns = useMemo(() => {
    const cols = []
    cols.push({
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (user, record) => (
        <div className="user-info" style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar src={record.avatar} className="user-avatar" style={{ marginRight: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {user?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div className="user-details" style={{ display: 'flex', flexDirection: 'column' }}>
            <span className="user-name" style={{ fontWeight: 500 }}>{user}</span>
          </div>
        </div>
      )
    })

    if (selectedType !== 'sharebot') {
      const hasModelData = selectedType === 'ai-image'
      cols.push({
        title: hasModelData ? 'Model Used' : 'Title',
        key: hasModelData ? 'model' : 'title',
        render: (_, record) => {
          return (
            record.model ? <Tag color="geekblue" style={{ borderRadius: '4px', border: 'none', background: '#f0f5ff', padding: '2px 8px', fontSize: '13px', margin: 0 }} title={record.model}>
              {formatModelName(record.model)}
            </Tag> : (
              <Tooltip title={record.title} placement="topLeft">
                <span style={{
                  display: 'inline-block',
                  maxWidth: '220px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  verticalAlign: 'middle',
                  color: '#111827',
                  fontWeight: 500
                }}>
                  {record.title}
                </span>
              </Tooltip>
            )
          )
        }
      })
    }
    cols.push({
      title: (
        <Select
          showSearch
          allowClear
          bordered={false}
          style={{ width: '100%', minWidth: 150 }}
          placeholder={<span style={{ color: '#111827', fontWeight: 500 }}>Organization</span>}
          optionFilterProp="children"
          value={selectedOrg || undefined}
          onChange={onOrgChange}
          filterOption={(input, option) => {
            const child = option.props.children;
            return child && String(child).toLowerCase().includes(input.toLowerCase());
          }}
        >
          {orgList?.map(org => (
            <Option key={org.id} value={org.id}>{org.title || org.name}</Option>
          ))}
        </Select>
      ),
      dataIndex: 'organization',
      key: 'organization'
    })
    if (selectedType === 'podcast') {
      cols.push({
        title: 'Duration (seconds)',
        dataIndex: 'duration',
        key: 'duration'
      })
    }
    if (selectedType === 'course') {
      cols.push({
        title: 'Total Pages',
        dataIndex: 'totalPages',
        key: 'totalPages'
      })
    }

    if (selectedType === 'maze') {
      cols.push({
        title: 'Depth',
        dataIndex: 'depth',
        key: 'depth'
      })
    }

    let avgCostTitle = "Average Cost"
    if (selectedType === 'ai-image') avgCostTitle = "Average Cost (per image)"
    else if (selectedType === 'ai_video') avgCostTitle = "Average Cost (per video)"
    else if (selectedType === 'podcast') avgCostTitle = "Average Cost (per second)"
    else if (selectedType === 'maze') avgCostTitle = "Average Cost (per depth)"
    else if (selectedType === 'course') avgCostTitle = "Average Cost (per page)"
    else if (selectedType === 'sharebot') avgCostTitle = "Average Cost (per question)"

    cols.push({
      title: avgCostTitle,
      dataIndex: 'avgCost',
      key: 'avgCost',
      render: avgCost => {
        if (typeof avgCost === 'number') {
          return avgCost > 0 && avgCost < 1
            ? Number(avgCost).toLocaleString(undefined, { maximumFractionDigits: 6 })
            : Number(avgCost).toLocaleString()
        }
        return avgCost
      }
    })
    cols.push({
      title: 'Total Cost',
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: totalCost => {
        if (typeof totalCost === 'number') {
          return totalCost > 0 && totalCost < 1
            ? Number(totalCost).toLocaleString(undefined, { maximumFractionDigits: 6 })
            : Number(totalCost).toLocaleString()
        }
        return totalCost
      }
    })
    if (selectedType === 'course') {
      cols.push({
        title: 'Elearning Course',
        key: 'elearningCourse',
        dataIndex: 'elearningCourse',
        render: val => {
          if (typeof val === 'number') {
            return val > 0 && val < 1 ? Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 }) : Number(val).toLocaleString()
          }
          return val
        }
      })
    }

    return cols
  }, [selectedType, orgList, selectedOrg, onOrgChange])

  return (
    <Card className="top-cost-users-card">
      <div className="top-cost-users-content">
        <div className="section-header" style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
          <h3 className="section-title" style={{ margin: 0, marginRight: 'auto' }}>{title}</h3>

          {activeTypes.length > 0 && (
            <div className="type-filter-container" style={{ display: 'flex', alignItems: 'center' }}>
              <span className="type-label" style={{ marginRight: '8px' }}>Type: </span>
              <Select
                value={selectedType}
                onChange={onTypeChange}
                className="type-select"
                style={{ width: 160 }}
                placeholder="All Services"
              >
                {activeTypes.map(type => {
                  const colorMap = {
                    'ai-image': '#5B6EE2',
                    'ai_video': '#C9D3FA',
                    audio: '#3875F6',
                    course: '#44959A',
                    podcast: '#EB3B88',
                    maze: '#9FD4FB',
                    sharebot: '#2DCCB9'
                  }

                  let displayName = type.charAt(0).toUpperCase() + type.slice(1)
                  if (type === 'ai-image') displayName = 'AI-Image'
                  else if (type === 'ai_video') displayName = 'Ai-Video'

                  return (
                    <Option key={type} value={type}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colorMap[type] || '#888', display: 'inline-block' }} />
                        {displayName}
                      </div>
                    </Option>
                  )
                })}
              </Select>
            </div>
          )}
        </div>

        <div className="filters-section">
          <div className="time-filters">
            {timeFilters.map(filter => (
              <Button
                key={filter}
                type={tableFilter === filter ? 'primary' : 'default'}
                onClick={() => onTableFilterChange(filter)}
                className="time-filter-button"
              >
                {filter}
              </Button>
            ))}
          </div>
          <div className="header-actions">
            <Button
              onClick={onExportClick}
              disabled={tableData.length === 0}
            >
              <DownloadOutlined /> Export Report
            </Button>
          </div>
        </div>

        <div className="table-section">
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={{
                ...pagination,
                onChange: onPageChange,
                onShowSizeChange: onPageChange,
                showSizeChanger: false,
                pageSizeOptions: [10, 20, 50, 100]
              }}
              className="users-table"
            />
          </Spin>
        </div>
      </div>
    </Card>
  )
})

export default TopCostUsers
